import { config } from "@/config";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { useAuthHeaders } from "@/hooks/use-auth-headers";

// Graph API configuration
const GRAPH_API_CONFIG = {
  endpoint: `${config.isometricApiUrl}/graph`,
};


export interface Neo4jNode {
  id: string;
  labels: string[];
  properties: Record<string, any>;
}

export interface Neo4jEdge {
  id: string;
  from: string;
  to: string;
  type: string;
  properties: Record<string, any>;
}

export interface GraphData {
  nodes: Neo4jNode[];
  edges: Neo4jEdge[];
}


// Helper function to convert graph API records to our format
const processApiRecords = (apiData: any): GraphData => {
  const nodes: Neo4jNode[] = [];
  const edges: Neo4jEdge[] = [];
  const nodeIds = new Set<string>();
  const edgeIds = new Set<string>();

  // Check if apiData has records property or if it's the raw data
  const records = apiData?.records || apiData || [];
  
  if (Array.isArray(records)) {
    records.forEach((record: any) => {
      try {
        // Check for optimized cleanPath format (server-side transformed data)
        // The cleanPath object can be in record.cleanPath or record._fields[0]
        const cleanPath = record.cleanPath || record._fields?.[0];

        if (cleanPath?.nodes || cleanPath?.relationships) {
          // Process pre-transformed nodes
          if (cleanPath.nodes && Array.isArray(cleanPath.nodes)) {
            cleanPath.nodes.forEach((node: any) => {
              if (!nodeIds.has(node.id.toString())) {
                nodes.push({
                  id: node.id.toString(),
                  labels: node.labels || [],
                  properties: node.properties || {},
                });
                nodeIds.add(node.id.toString());
              }
            });
          }

          // Process pre-transformed relationships
          if (cleanPath.relationships && Array.isArray(cleanPath.relationships)) {
            cleanPath.relationships.forEach((rel: any) => {
              if (!edgeIds.has(rel.id.toString())) {
                edges.push({
                  id: rel.id.toString(),
                  from: rel.start.toString(),
                  to: rel.end.toString(),
                  type: rel.type || 'UNKNOWN',
                  properties: rel.properties || {},
                });
                edgeIds.add(rel.id.toString());
              }
            });
          }
          
          return; // Skip the old processing for this record
        }
        
        // Fallback to old processing for backward compatibility
        // Handle different possible API response formats
        let pathData = null;
        
        // Format 1: Direct path object
        if (record.path) {
          pathData = record.path;
        }
        // Format 2: Record has path property
        else if (record._fields && record._fields[0] && record._fields[0].segments) {
          pathData = record._fields[0];
        }
        // Format 3: Direct segments array
        else if (record.segments) {
          pathData = record;
        }
        // Format 4: Check if record itself has the data we need
        else if (record.start && record.end) {
          // This looks like a direct relationship
          if (!edgeIds.has(record.identity?.toString() || `${record.start?.identity}-${record.end?.identity}`)) {
            const edgeId = record.identity?.toString() || `${record.start?.identity}-${record.end?.identity}`;
            edges.push({
              id: edgeId,
              from: record.start?.identity?.toString() || record.start?.toString(),
              to: record.end?.identity?.toString() || record.end?.toString(),
              type: record.type || 'UNKNOWN',
              properties: record.properties || {},
            });
            edgeIds.add(edgeId);
          }
        }
        
        if (pathData && pathData.segments) {
          pathData.segments.forEach((segment: any) => {
            // Process start node
            if (segment.start && !nodeIds.has(segment.start.identity?.toString() || segment.start.toString())) {
              const nodeId = segment.start.identity?.toString() || segment.start.toString();
              nodes.push({
                id: nodeId,
                labels: segment.start.labels || [],
                properties: segment.start.properties|| {} ,
              });
              nodeIds.add(nodeId);
            }

            // Process end node
            if (segment.end && !nodeIds.has(segment.end.identity?.toString() || segment.end.toString())) {
              const nodeId = segment.end.identity?.toString() || segment.end.toString();
              nodes.push({
                id: nodeId,
                labels: segment.end.labels || [],
                properties: segment.end.properties|| {},
              });
              nodeIds.add(nodeId);
            }

            // Process relationship
            if (segment.relationship && !edgeIds.has(segment.relationship.identity?.toString() || `rel-${segment.start?.identity}-${segment.end?.identity}`)) {
              const edgeId = segment.relationship.identity?.toString() || `rel-${segment.start?.identity}-${segment.end?.identity}`;
              edges.push({
                id: edgeId,
                from: segment.relationship.start?.toString() || segment.start?.identity?.toString(),
                to: segment.relationship.end?.toString() || segment.end?.identity?.toString(),
                type: segment.relationship.type || 'UNKNOWN',
                properties: segment.relationship.properties || {},
              });
              edgeIds.add(edgeId);
            }
          });
        }

      } catch (error) {
        console.error('Error processing API record:', error, record);
      }
    });
  } else {
    console.warn('API response is not an array:', records);
  }

  return { nodes, edges };
};


// API query function
const queryWithApi = async (
  projectUuid: string,
  authHeaders: Record<string, string>,
): Promise<GraphData> => {
  // Root view: the project, its ontology roots, and one level under each.
  //
  // This deliberately replaces `(p)-[r*0..4]-(n) ... LIMIT 100`. That pattern
  // enumerated variable-length paths and then cut at 100 *paths*, not nodes — so
  // with ~200 outcomes under a single persona the functional branch consumed the
  // whole budget and Design/Architectural never appeared. It was also undirected,
  // so it walked back out of the project, and depth 4 stopped mid-functional
  // (Project→Functional→Persona→Outcome→Scenario) without ever reaching Step or
  // Action. Depth 2 and a direction give a small, complete, predictable set:
  // three roots plus their immediate children — 32 nodes and ~100ms on the dev
  // graph. The UI draws only the roots from this and uses the second level
  // purely to count each root's children, so it can label the expand action and
  // tell a genuinely empty branch from an unopened one. Everything deeper is
  // lazy-loaded one level per click.
  //
  // Note there is no code branch here: repositories live in PostgreSQL and have
  // no edge from Project, so no traversal can reach them.
  //
  // The UNWIND/CASE dance keeps a project with no ontology yet from returning
  // zero rows — nodes(null) is null, so the project alone is emitted instead.
  const cypher = `
    MATCH (p:Project {uuid: $projectUuid})
    OPTIONAL MATCH path = (p)-[*1..2]->(n)
    WITH p, collect(path) AS paths
    UNWIND (CASE WHEN size(paths) = 0 THEN [null] ELSE paths END) AS path
    WITH CASE WHEN path IS NULL THEN [p] ELSE nodes(path) END AS pathNodes,
         CASE WHEN path IS NULL THEN [] ELSE relationships(path) END AS pathRels
    RETURN {
      nodes: [node IN pathNodes | {
        id: COALESCE(node.id, elementId(node)),
        labels: labels(node),
        properties: node {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }],
      relationships: [rel IN pathRels | {
        id: elementId(rel),
        start: COALESCE(startNode(rel).id, elementId(startNode(rel))),
        end: COALESCE(endNode(rel).id, elementId(endNode(rel))),
        type: type(rel),
        properties: rel {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }]
    } as cleanPath
    LIMIT 500
  `;

  try {
    const response = await apiFetch(GRAPH_API_CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        query: cypher,
        params: { projectUuid }
      }),
    });

    if (!response.ok) {
      throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    // Process the pre-transformed cleanPath data from server
    return processApiRecords(result.records || result);
  } catch (error) {
    console.error('Graph query failed:', error);
    throw new Error(`Failed to fetch graph data: ${error}`);
  }
};

// BREEZEAI-1136 DEF-01: node labels that carry a `id` range index. An
// unlabelled `MATCH (target) WHERE target.id = $nodeId` degrades to a full
// scan across every node in the graph (including hundreds of thousands of
// Statement rows) — measured at ~50 s, which the ingress then converts to a
// 504, which Chromium reports to the user as a CORS error. Every entry here
// is a label with a range index on `id` (persona_id_unique, outcome_id_unique,
// …), so a labelled point-lookup lands in single-digit ms. Statement is
// deliberately excluded — it has no index and expanding one is refused
// elsewhere in this file for the same reason.
const INDEXED_ID_LABELS: readonly string[] = [
  "Project",
  "Functional",
  "Persona",
  "Outcome",
  "Scenario",
  "Step",
  "Action",
  "Api",
  "Design",
  "UserJourney",
  "Flow",
  "Page",
  "Component",
  "Document",
  "Code",
  "Repository",
  "File",
  "Class",
  "Function",
  "Architectural",
  "UserExperience",
  "ApiGateway",
  "ObservabilityMonitoring",
  "Agents",
  "Services",
  "EventQueue",
  "DataLake",
  "Infrastructure",
  "DDLTable",
  "DDLColumn",
  "DDLConstraint",
  "DDLIndex",
  "DDLView",
  "DDLProcedure",
  "DDLSequence",
  "ESIndex",
  "ESField",
  "ESAlias",
  "NoSQLCollection",
  "NoSQLField",
  "NoSQLIndex",
  "GraphNodeType",
  "GraphEdgeType",
  "GraphIndex",
];
const INDEXED_ID_LABEL_SET = new Set(INDEXED_ID_LABELS);

const buildResolveAnchorCypher = (): string => {
  const labelledBranches = INDEXED_ID_LABELS.map(
    (l) => `  MATCH (n:${l} {id: $nodeId}) RETURN n LIMIT 1`,
  );
  // Final branch catches nodes that don't carry an `id` property (Statement is
  // the notable one — no id index, no id property; its "id" in graphData is
  // COALESCE'd from `elementId(node)`). `NodeByElementIdSeek` is an O(1)
  // internal pointer lookup, not a scan, so this branch is cheap even though
  // it runs on every resolve.
  labelledBranches.push(
    `  MATCH (n) WHERE elementId(n) = $nodeId RETURN n LIMIT 1`,
  );
  return labelledBranches.join("\n  UNION\n");
};

// API query function for specific node with neighbors
const queryNodeWithNeighbors = async (
  nodeId: string,
  authHeaders: Record<string, string>,
  label?: string,
): Promise<GraphData> => {
  // Two anchor strategies, both landing at the same `path = (target)-[*0..1]-(neighbor)`
  // traversal (which is fast once `target` is bound).
  //   * label known  → single labelled point-lookup, index-hit, <10 ms.
  //   * label absent → UNION of labelled point-lookups (each an index-hit),
  //                    total still in the ~100 ms range, LIMIT 1 short-circuits.
  const safeLabel =
    label && INDEXED_ID_LABEL_SET.has(label) ? label : undefined;
  const anchor = safeLabel
    ? `MATCH (target:${safeLabel} {id: $nodeId}) WITH target LIMIT 1`
    : `CALL {\n${buildResolveAnchorCypher()}\n} WITH n AS target LIMIT 1`;

  const cypher = `
    ${anchor}
    MATCH path = (target)-[r*0..1]-(neighbor)
    WITH nodes(path) as pathNodes, relationships(path) as pathRels
    RETURN {
      nodes: [node IN pathNodes | {
        id: COALESCE(node.id, elementId(node)),
        labels: labels(node),
        properties: node {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }],
      relationships: [rel IN pathRels | {
        id: elementId(rel),
        start: COALESCE(startNode(rel).id, elementId(startNode(rel))),
        end: COALESCE(endNode(rel).id, elementId(endNode(rel))),
        type: type(rel),
        properties: rel {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }]
    } as cleanPath
    LIMIT 50
  `;

  try {
    const response = await apiFetch(GRAPH_API_CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        query: cypher,
        params: { nodeId }
      }),
    });

    if (!response.ok) {
      throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    // Process the pre-transformed cleanPath data from server
    return processApiRecords(result.records || result);
  } catch (error) {
    console.error('Node neighbors query failed:', error);
    throw new Error(`Failed to fetch node neighbors: ${error}`);
  }
};

/**
 * Fetch one level of children below a node, for lazy expansion.
 *
 * Deliberately unfiltered by label: unlike the code ontology — where the map of
 * allowed child labels exists to stop IMPORTS edges hopping into a neighbouring
 * file's tree — the knowledge graph's hierarchy is already clean HAS_* edges.
 * Taking every outgoing edge means one implementation covers functional, design
 * and architecture, including the joins that are not parent/child at all:
 * Scenario-[:MAPS_TO]->UserJourney into the design graph, and
 * DDLTable-[:REFERENCES]->DDLTable between tables.
 *
 * The parent's LABEL is required, and that is not a detail — it is the whole
 * performance story. Every ontology label carries a range index on `id`
 * (persona_id_unique, outcome_id_unique, ...), but Neo4j can only choose one
 * when the pattern names a label AND compares the raw property. An unlabelled
 * `MATCH (parent) WHERE COALESCE(parent.id, ...) = $nodeId` defeats both
 * conditions and degrades to a scan of every node in the database — including
 * the hundreds of thousands of code-ontology Statement nodes. Measured on the
 * dev graph: 53,000ms unlabelled versus 69ms labelled, for the same 8 rows.
 *
 * Labels cannot be parameterised in Cypher, so the label is interpolated and
 * must be validated by the caller before it gets here.
 */
/** A Neo4j label safe to interpolate: letters, digits and underscores only. */
const isSafeLabel = (label: string): boolean => /^[A-Za-z][A-Za-z0-9_]*$/.test(label);

/**
 * Rows returned by one expansion. An ESIndex can carry thousands of fields and a
 * DataLake hundreds of tables, so the cap is real and the caller is told when it
 * was hit — showing 300 of 5,000 fields with no warning reads as "that's all
 * there is", which is the worst kind of wrong.
 */
const EXPAND_LIMIT = 300;

/**
 * Cross-ontology edges, deliberately NOT followed when expanding.
 *
 * These are real and useful, but they are not containment: Scenario -MAPS_TO->
 * UserJourney, Step -MAPS_TO-> Flow/Page, Action -IMPLEMENTED_BY-> Component all
 * jump from the functional tree into the design tree. Following them meant the
 * same design node could appear in two places at once, under Design and again
 * under whichever Step happened to reference it. Design is now reached from its
 * own root instead, and the functional side stays a clean hierarchy.
 *
 * The link is not lost: a UserJourney still carries `scenarioId`, and Flow, Page
 * and Component carry their functional ids, all visible in the details panel.
 */
const EXCLUDED_EXPANSION_RELS = ['MAPS_TO', 'IMPLEMENTED_BY'];

const queryNodeChildren = async (
  nodeId: string,
  label: string,
  authHeaders: Record<string, string>
): Promise<{ graph: GraphData; truncated: boolean }> => {
  if (!isSafeLabel(label)) {
    throw new Error(`Cannot expand a node with an unusable label: ${label}`);
  }

  const cypher = `
    MATCH (parent:\`${label}\` { id: $nodeId })
    MATCH (parent)-[r]->(child)
    WHERE NOT type(r) IN $excludedRels
    RETURN {
      nodes: [node IN [parent, child] | {
        id: COALESCE(node.id, elementId(node)),
        labels: labels(node),
        properties: node {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }],
      relationships: [{
        id: elementId(r),
        start: COALESCE(startNode(r).id, elementId(startNode(r))),
        end: COALESCE(endNode(r).id, elementId(endNode(r))),
        type: type(r),
        properties: r {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }]
    } as cleanPath
    LIMIT ${EXPAND_LIMIT}
  `;

  const response = await apiFetch(GRAPH_API_CONFIG.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ query: cypher, params: { nodeId: String(nodeId), excludedRels: EXCLUDED_EXPANSION_RELS } }),
  });

  if (!response.ok) {
    throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  const records = result.records || result;
  return {
    graph: processApiRecords(records),
    truncated: Array.isArray(records) && records.length >= EXPAND_LIMIT,
  };
};

/**
 * Lazy-expansion counterpart to useGetGraphData. A mutation rather than a query
 * because it is fired by a click and its result is merged into the live
 * vis-network DataSet, not cached per node.
 */
/**
 * Files belonging to one repository.
 *
 * This is the seam between the two stores. A repository is a PostgreSQL row and
 * has no node in Neo4j — nothing in the graph points at it — so the tree cannot
 * be walked across this boundary. The repo's `_id` is carried over as
 * `codeOntologyId`, which every File node holds and `file_codeOntologyId_index`
 * covers. The parent edges are synthesised by the UI, since none exist in Neo4j.
 */
const queryRepoFiles = async (
  codeOntologyId: number,
  authHeaders: Record<string, string>
): Promise<{ graph: GraphData; truncated: boolean }> => {
  const cypher = `
    MATCH (f:File { codeOntologyId: $codeOntologyId })
    RETURN {
      nodes: [{
        id: COALESCE(f.id, elementId(f)),
        labels: labels(f),
        properties: f {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }],
      relationships: []
    } as cleanPath
    ORDER BY f.path
    LIMIT ${EXPAND_LIMIT}
  `;

  const response = await apiFetch(GRAPH_API_CONFIG.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ query: cypher, params: { codeOntologyId } }),
  });

  if (!response.ok) {
    throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  const records = result.records || result;
  return {
    graph: processApiRecords(records),
    truncated: Array.isArray(records) && records.length >= EXPAND_LIMIT,
  };
};

export const useGetRepoFiles = (
  onSuccess: (data: GraphData, repoNodeId: string, truncated: boolean) => void,
  onError: (error: Error) => void
) => {
  const { getAuthHeaders } = useAuthHeaders();
  return useMutation({
    mutationFn: ({ codeOntologyId, repoNodeId }: { codeOntologyId: number; repoNodeId: string }) =>
      queryRepoFiles(codeOntologyId, getAuthHeaders(false)).then((r) => ({ ...r, repoNodeId })),
    onSuccess: ({ graph, repoNodeId, truncated }) => onSuccess(graph, repoNodeId, truncated),
    onError: (error: Error) => onError(error),
    // BREEZEAI-1136 TC-33 (DEF-04): default `networkMode: "online"` PAUSES
    // mutations when offline instead of failing them, so onError never fires
    // and the user gets no feedback. Force the fetch to attempt anyway so it
    // rejects with a network error and the existing toast.error triggers.
    networkMode: "always",
  });
};

/**
 * Every UserJourney in a project.
 *
 * Design does not own its journeys in the graph — its only outgoing edge is
 * HAS_WEBSITE, and a UserJourney's parent key is `scenarioId`. So the journeys
 * cannot be reached by walking down from Design; they are collected by
 * projectUuid instead (covered by userjourney_projectUuid_index) and hung off
 * the Design root by the UI. From a journey down, INCLUDES_FLOW -> CONTAINS_PAGE
 * -> CONTAINS is ordinary containment and needs no special handling.
 */
const queryProjectUserJourneys = async (
  projectUuid: string,
  authHeaders: Record<string, string>
): Promise<GraphData> => {
  const cypher = `
    MATCH (uj:UserJourney { projectUuid: $projectUuid })
    RETURN {
      nodes: [{
        id: COALESCE(uj.id, elementId(uj)),
        labels: labels(uj),
        properties: uj {.*, embedding: null, embeddingText: null, embeddings: null, vector: null, vectors: null}
      }],
      relationships: []
    } as cleanPath
    ORDER BY uj.name
    LIMIT ${EXPAND_LIMIT}
  `;

  const response = await apiFetch(GRAPH_API_CONFIG.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ query: cypher, params: { projectUuid } }),
  });

  if (!response.ok) {
    throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return processApiRecords(result.records || result);
};

/**
 * Loaded with the page, like the repository list: journeys number in the tens, and
 * knowing the count up front is what lets the Design root be labelled with its
 * size and hidden entirely when a project has no design yet.
 */
export const useGetProjectUserJourneys = (projectUuid: string, enabled: boolean = true) => {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: ['project-user-journeys', projectUuid],
    queryFn: () => queryProjectUserJourneys(projectUuid, getAuthHeaders(false)),
    enabled: enabled && !!projectUuid,
  });
};

export const useExpandNodeChildren = (
  onSuccess: (data: GraphData, nodeId: string, truncated: boolean) => void,
  onError: (error: Error) => void
) => {
  const { getAuthHeaders } = useAuthHeaders();
  return useMutation({
    mutationFn: ({ nodeId, label }: { nodeId: string; label: string }) =>
      queryNodeChildren(nodeId, label, getAuthHeaders(false)).then((r) => ({ ...r, nodeId })),
    onSuccess: ({ graph, nodeId, truncated }) => onSuccess(graph, nodeId, truncated),
    onError: (error: Error) => onError(error),
    // BREEZEAI-1136 TC-33 (DEF-04): see useGetRepoFiles — force attempt when
    // offline so the caller's onError toast fires instead of a silent pause.
    networkMode: "always",
  });
};

export const useGetGraphData = (projectUuid: string, enabled: boolean = true, sessionId: number = 0) => {
  const { getAuthHeaders, isAuthenticated } = useAuthHeaders();
  return useQuery({
    queryKey: ["graph-data", projectUuid, sessionId],
    queryFn: async (): Promise<GraphData> => {
      try {
        return await queryWithApi(projectUuid, getAuthHeaders(false));
      } catch (error) {
        console.error('Graph query error:', error);
        throw new Error(`Failed to fetch graph data: ${error}`);
      }
    },
    enabled: enabled && !!projectUuid && isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds - shorter to ensure fresh data
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnReconnect: true,
  });
};

export const useGetNodeWithNeighbors = (
  nodeId: string,
  enabled: boolean = true,
  sessionId: number = 0,
  label?: string,
) => {
  const { getAuthHeaders, isAuthenticated } = useAuthHeaders();
  return useQuery({
    // label is part of the cache key — a different label with the same id
    // is a different query (and no-label vs label uses different Cypher).
    queryKey: ["node-neighbors", nodeId, label, sessionId],
    queryFn: async (): Promise<GraphData> => {
      try {
        return await queryNodeWithNeighbors(nodeId, getAuthHeaders(false), label);
      } catch (error) {
        console.error('Node neighbors query error:', error);
        throw new Error(`Failed to fetch node neighbors: ${error}`);
      }
    },
    enabled: enabled && !!nodeId && isAuthenticated,
    staleTime: 0, // Always treat data as stale to ensure fresh queries
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnReconnect: true,
    // BREEZEAI-1136 TC-17 (DEF-01): the /graph endpoint can fail via CORS/504
    // when the focus subquery is slow — retrying 3 times (react-query default)
    // keeps the "Loading knowledge graph..." spinner up for 7+ seconds before
    // the error UI ever appears. Fail fast so the user sees the retry button
    // and can decide, and so the focus-mode fallback in the visualizer kicks
    // in promptly.
    retry: 1,
    networkMode: "always",
  });
};